import axios from 'axios';
import { Product } from '../interfaces/Product';
import { FacetList, facetValue } from '../interfaces/Filter';
import { computed } from 'vue';
import store from '@/store';
import qs from 'qs';

export const getProductsAndFilters = async (data: { storeId: string, categoryId: string, catalogId: string, searchHostName: string, pageNumber: number, pageSize?: number, isSearchPage: boolean, inStockFacet: string }, facet?: string, orderBy?: string) => {
    const searchTerm = getSearchTermFromUrl();
    const url = createUrl(data, searchTerm);

    try {
        const params = buildParams(data, facet, searchTerm, orderBy);
        const serializedParams = qs.stringify(params, {
            arrayFormat: 'repeat',
            encode: false, // Questo evita la codifica automatica per tutti i parametri
        });
        const response = await axios.get(url, {
            params: params,
            paramsSerializer: () => serializedParams,
            withCredentials: false,
        });

        return {
            products: store.getters.configurationParams.isFlatPLP
                ? mapFlatProducts(response.data.catalogEntryView)
                : mapProducts(response.data.catalogEntryGroupView),

            facets: response.data.facetView?.length
                ? mergeFacets(store.getters.defaultFacet, mapFilter(response.data.facetView))
                : mergeFacets(store.getters.defaultFacet, mapFilter([])),

            recordSetTotal: response.data.recordSetTotal
        };
    } catch (error) {
        console.error("Error fetching products:", error);
        throw error;
    }
};
export const getDefaultFilters = async (data: { storeId: string, categoryId: string, catalogId: string, searchHostName: string, pageNumber: number, pageSize?: number, isSearchPage: boolean, inStockFacet: string }) => {
    const searchTerm = getSearchTermFromUrl();
    const url = createUrl(data, searchTerm);

    try {
        const params = buildParams(data, searchTerm);
        const serializedParams = qs.stringify(params, {
            arrayFormat: 'repeat',
            encode: false, // Questo evita la codifica automatica per tutti i parametri
        });
        const response = await axios.get(url, {
            params: params,
            paramsSerializer: () => serializedParams,
            withCredentials: false,
        });

        //rimuovo le entry con count 0
        const cleanedData = mapFilter(response.data.facetView).map(item => ({
            ...item,
            entry: item.entry.filter(entry => entry.count.toString() !== '0')
        }));

        return {
            facets: cleanedData
        };
    } catch (error) {
        console.error("Error fetching products:", error);
        throw error;
    }
};

function createUrl(data: { storeId: string, categoryId: string, searchHostName: string, isSearchPage: boolean }, searchTerm: string) {
    if (data.isSearchPage) {
        return `${data.searchHostName}/search/resources/store/${data.storeId}/productview/bySearchTerm/${searchTerm}`;
    } else {
        return `${data.searchHostName}/search/resources/store/${data.storeId}/productview/byCategory/${data.categoryId}`;
    }
}

type ParamsType = {
    profileName: string;
    catalogId: string;
    langId: string;
    pageSize: number;
    pageNumber: number;
    orderBy?: string;
    metaData: string;
    searchTerm?: string;
    categoryId?: string;
    facet?: string | string[]; // Può essere una stringa singola o un array di stringhe
};

const buildParams = (
    data: { storeId: string; categoryId: string; catalogId: string; searchHostName: string; pageNumber: number; pageSize?: number; isSearchPage: boolean; inStockFacet: string },
    facet?: string,
    searchTerm?: string,
    orderBy?: string
): ParamsType => {
    const params: ParamsType = {
        profileName: data.isSearchPage
            ? "RONA_findProductsAndSkusBySearchTerm"
            : store.getters.configurationParams.isSeoCustomPLP
                ? "RONA_findProductsAndSkusByCategoryCustom__group"
                : store.getters.configurationParams.isFlatPLP
                    ? "RONA_findProductsAndSkusByCategory_flat"
                    : "RONA_findProductsAndSkusByCategory_group",
        catalogId: data.catalogId,
        langId: "-1",
        pageSize: data.pageSize ?? 1,
        pageNumber: data.pageNumber,
        orderBy: orderBy,
        metaData: '',
    };

    if (data.isSearchPage) {
        params.searchTerm = searchTerm;
    } else {
        params.categoryId = data.categoryId;
    }

    // Aggiungi la proprietà facet
    params.facet = facet ? [facet, `${data.inStockFacet}:'1'`] : [`${data.inStockFacet}:'1'`];

    return params;
};


const getSearchTermFromUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("searchTerm") || "";
};

function mapProducts(data: Record<string, any[]>): Product[] {
    // Verifica che data sia un oggetto e contiene almeno un array non vuoto
    return Object.values(data)
        .filter((itemsArray) => itemsArray.length > 0) // Considera solo gli array non vuoti
        .map((itemsArray) => {
            const item = itemsArray[0]; // Usa il primo elemento di ciascun array
            const colorsNumber = (itemsArray.length).toString();
            return mapItem(item, colorsNumber);
        });
}

function mapFlatProducts(data: Record<string, any[]>): Product[] {
    return Object.values(data)
        .map((item) => {
            return mapItem(item, "");
        });

}

function mapItem(item: Record<string, any>, colorsNumber: string): Product {
    let offerPrice: string;

    if (store.getters.configurationParams.earlyAccessEnabled) {
        offerPrice = Number(item.sKUs[0].price.find((obj: any) => obj.description === "EA")?.value).toFixed(2);
    } else {
        offerPrice = Number(item.sKUs[0].price.find((obj: any) => obj.description === "I")?.value).toFixed(2);
    }

    return {
        uniqueId: item.sKUs[0].uniqueID,
        brand: item.manufacturer,
        colorsNumber: colorsNumber,
        listPrice: Number(item.sKUs[0].price.find((obj: any) => obj.description === "L")?.value).toFixed(2),
        offerPrice: offerPrice,
        earlyAccessPrice: Number(item.sKUs[0].price.find((obj: any) => obj.description === "EA")?.value).toFixed(2),
        partNumber: item.sKUs[0].partNumber,
        productAttributes: new Map<string, string>(
            item.attributes.map((attribute: any) => [
                attribute.identifier,
                attribute.values && attribute.values[0] && attribute.values[0].value ? attribute.values[0].value : ""
            ])
        ),
        itemAttributes: new Map<string, string>(
            item.sKUs[0].attributes.map((attribute: any) => [attribute.identifier,
            attribute.values && attribute.values[0] && attribute.values[0].value ? attribute.values[0].value : ""
            ])
        ),
        parentCatalogGroupID: item.sKUs[0].parentCatalogGroupID,
        buyable: item.sKUs[0].buyable,
        firstImgUrl: store.getters.configurationParams.amlImageHost + item.sKUs[0].thumbnail,
        secondImgUrl: store.getters.configurationParams.amlImageHost + item.sKUs[0].fullImage,
    };
}

export async function getCategoriesMap(categories: string[]) {
    const baseUrl = `${store.getters.configurationParams.searchHostName}/search/resources/store/${store.getters.configurationParams.storeId}/categoryview/`;
    const params = `?catalogId=${store.getters.configurationParams.catalogId}&langId=${store.getters.configurationParams.langId}&storeId=${store.getters.configurationParams.storeId}`;
    const resultMap = new Map();
    const promises = categories.map(category => {
        const url = `${baseUrl}${category}${params}`;
        return axios.get(url).then(response => ({
            category,
            data: response.data,
        })).catch(error => {
            console.error(`Error during fetch for category ${category}:`, error);
            return null;
        });
    });

    const results = await Promise.all(promises);

    results.forEach(result => {
        if (result && result.data && result.data.catalogGroupView && result.data.catalogGroupView.length > 0) {
            const catalog = result.data.catalogGroupView[0];
            resultMap.set(catalog.uniqueID, catalog.identifier);
        }
    });

    return resultMap;
}

if (typeof window.mostClickedBrands == 'undefined') {
    window.mostClickedBrands = {
        "Persol": 1,
        "Arnette": 2,
        "Oakley": 3,
    };
}

const mostClickedBrands = window.mostClickedBrands;

function mapFilter(data: FacetList[]): FacetList[] {
    // Mappa per associare facet.name a label
    if (typeof window.labelFilterMap == 'undefined') {
        window.labelFilterMap = {
            "GENDER": "Gender",
            "VM_ENABLED": "Virtual Mirror",
            "BRAND": "Brand",
            "FRONT_COLOR_FACET_GL_US": "Frame Color",
            "FRAME_SHAPE_FACET_GL_US": "Shape",
            "HIGH_PRESCRIPTION": "Available with high prescription",
            "FRAME_TYPE": "Type",
            "FRAME_MATERIAL_FACET_GL_US": "Material",
            "OfferPrice_USD": "Price",
            "Sales": "% Promo",
            "LENS_COLOR_FACET_GL_US": "Lens Color",
            "LENS_TREATMENT_FACET_GL_US": "Lens Treatment",
            "POLARIZED": "Polarized",
        };
    }
    const labelMap = window.labelFilterMap;

    const typeFilterMap: { [key: string]: "default" | "price" | "dash" | "search" } = {
        "GENDER": "default",
        "VM_ENABLED": "dash",
        "BRAND": "search",
        "FRONT_COLOR_FACET_GL_US": "default",
        "FRAME_SHAPE_FACET_GL_US": "default",
        "HIGH_PRESCRIPTION": "dash",
        "FRAME_TYPE": "default",
        "FRAME_MATERIAL_FACET_GL_US": "default",
        "OfferPrice_USD": "price",
        "Sales": "default",
        "LENS_COLOR_FACET_GL_US": "default",
        "POLARIZED": "dash",
        "LENS_TREATMENT_FACET_GL_US": "default",
    };

    const genderFacet = data.find((facet) => facet.name === "GENDER");
    const macroAgeRangeFacet = data.find((facet) => facet.name === "MACRO_AGE_RANGE");

    // Se entrambi esistono, aggiungi solo le entry con label 'kids' da MACRO_AGE_RANGE a GENDER
    if (genderFacet && macroAgeRangeFacet) {
        const kidsEntries = macroAgeRangeFacet.entry.filter(entry => entry.label.toLocaleLowerCase() === "children");
        genderFacet.entry = [...genderFacet.entry, ...kidsEntries];
    }

    // Filtra i dati e aggiungi la proprietà label
    return data
        .filter((facet: FacetList) => Object.keys(labelMap).includes(facet.name))
        .map((facet: FacetList) => {
            // Se il facet è "BRAND", ordina le entry in base alla posizione definita in window.mostClickedBrands
            if (window.mostClickedBrands) {
                if (facet.name === "BRAND") {
                    // Separiamo i brand cliccati e non cliccati
                    const clickedBrands: facetValue[] = [];
                    const otherBrands: facetValue[] = [];

                    facet.entry.forEach((entry) => {

                        if (window.mostClickedBrands.hasOwnProperty(entry.label)) {
                            clickedBrands.push(entry);
                        } else {
                            otherBrands.push(entry);
                        }

                    });

                    // Ordina i brand cliccati in base alla loro priorità
                    clickedBrands.sort((a, b) => {
                        const aPriority = window.mostClickedBrands[a.label];
                        const bPriority = window.mostClickedBrands[b.label];
                        return aPriority - bPriority;
                    });

                    // Ordina alfabeticamente i brand non cliccati
                    otherBrands.sort((a, b) => a.label.localeCompare(b.label));

                    // Combina i brand cliccati e non cliccati
                    facet.entry = [...clickedBrands, ...otherBrands];


                }
                if (facet.name === 'OfferPrice_USD') {
                    facet.entry = facet.entry.map(entry => ({
                        ...entry,
                        label: customizeLabel(entry.label, facet.name) // Personalizza l'etichetta
                    }));
                }
                else if (facet.name === 'VM_ENABLED') {
                    facet.entry = facet.entry.filter(entry => entry.label.toLowerCase() === 'true');

                    // Se facet.entry è vuoto dopo il filtro, rimuoviamo il facet
                    if (facet.entry.length === 0) {
                        return null;
                    }
                    facet.entry = facet.entry.map(entry => ({
                        ...entry,
                        label: customizeLabel(entry.label, facet.name) // Personalizza l'etichetta
                    }));
                }
                else if (facet.name === 'HIGH_PRESCRIPTION') {
                    facet.entry = facet.entry.map(entry => ({
                        ...entry,
                        label: customizeLabel(entry.label, facet.name) // Personalizza l'etichetta
                    }));
                } else if (facet.name === 'POLARIZED') {
                    facet.entry = facet.entry.filter(entry => entry.label.toLowerCase() === 'true' && entry.count > 0);

                    // Se facet.entry è vuoto dopo il filtro, rimuoviamo il facet
                    if (facet.entry.length === 0) {
                        return null;
                    }

                    // Personalizza l'etichetta solo se il facet non è stato rimosso
                    facet.entry = facet.entry.map(entry => ({
                        ...entry,
                        label: customizeLabel(entry.label, facet.name)
                    }));
                }

            }

            return {
                ...facet, // Crea un nuovo oggetto con i dati originali
                label: labelMap[facet.name] || "Unknown", // Aggiungi la label
                type: typeFilterMap[facet.name] || "default", // Aggiungi il tipo
                entry: facet.entry.map(entry => ({
                    ...entry,
                    attributeLabel: facet.name == 'Sales' ? 'Promo' : facet.name // Aggiungi attributeLabel a ogni entry
                }))
            };
        }).filter(facet => facet !== null);
}

function customizeLabel(label: string, facetName: string): string {
    if (facetName === "OfferPrice_USD") {
        const match = label.match(/\{(\*|\d+(?:\.\d+)?)\s+(\*|\d+(?:\.\d+)?)\}/);
        if (match) {
            const min = match[1] === '*' ? null : parseFloat(match[1]);
            const max = match[2] === '*' ? null : parseFloat(match[2]);

            if (min === null && max !== null) {
                return `Under ${max.toFixed(2)}$`; // Es: "{* 149.99}" -> "under 149.99"
            } else if (min !== null && max !== null) {
                return `${min.toFixed(2)}$ - ${max.toFixed(2)}$`; // Es: "{150 249.99}" -> "150 - 249.99"
            } else if (min !== null && max === null) {
                return `${min.toFixed(2)}$ and up`; // Es: "{250 *}" -> "250 and up"
            }
        }
    } else if (facetName === 'VM_ENABLED') {
        return 'Virtual mirror'
    } else if (facetName === 'HIGH_PRESCRIPTION') {
        return 'High prescription'
    } else if (facetName === 'POLARIZED') {
        return 'Polarized'
    }
    return label;
}


function mergeFacets(defaultFacet: FacetList[], updatedFacet: FacetList[]): FacetList[] {
    // Crea una mappa rapida per cercare facet e entry di 'updatedFacet'
    const updatedFacetMap = new Map<string, facetValue[]>();

    // Popola la mappa con le entry di 'updatedFacet'
    updatedFacet.forEach(facet => {
        updatedFacetMap.set(facet.name, facet.entry);
    });

    // Crea un nuovo array di facet unendo 'defaultFacet' e 'updatedFacet' senza modificare 'defaultFacet'
    const mergedFacets = defaultFacet.map(defaultFacetItem => {
        // Ottieni le entry aggiornate dall'updatedFacet, se presenti
        const updatedEntries = updatedFacetMap.get(defaultFacetItem.name) || [];

        // Crea una mappa per cercare rapidamente le entry di 'updatedFacet' per valore
        const updatedEntriesMap = new Map<string, facetValue>(
            updatedEntries.map(entry => [entry.value, entry])
        );

        // Confronta le entry di 'defaultFacet' con quelle di 'updatedFacet'
        const mergedEntries = defaultFacetItem.entry.map(defaultEntry => {
            const updatedEntry = updatedEntriesMap.get(defaultEntry.value);

            if (updatedEntry) {
                // Se l'entry esiste in 'updatedFacet', crea una nuova entry con il count aggiornato
                return {
                    ...defaultEntry,
                    count: updatedEntry.count, // Aggiorna il count
                    disabled: updatedEntry.count == 0 ? true : false,
                    attributeLabel: defaultFacetItem.label
                };
            } else {
                // Se non esiste, crea una nuova entry con count a 0 e 'disabled' impostato a true
                return {
                    ...defaultEntry,
                    count: 0,
                    disabled: true,
                    attributeLabel: defaultFacetItem.label
                };
            }
        });

        // Ritorna un nuovo oggetto facet con le entry aggiornate
        return {
            ...defaultFacetItem,
            entry: mergedEntries,
        };
    });

    return mergedFacets;
}

export async function getBrandSeoUrl(brandIdentifier: string) {
    const encodedBrandIdentifier = encodeURIComponent(brandIdentifier);

    const url = `https://${store.getters.configurationParams.hostName}/wcs/resources/store/${store.getters.configurationParams.storeId}/catalog/${store.getters.configurationParams.catalogId}/seourl/brand/${encodedBrandIdentifier}`;

    try {
        const response = await axios.get(url);

        return response.data.response.SEOUrl;
    } catch (error) {
        console.error("Error fetching brand url:", error);
        throw error;
    }
}






